import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { RootState, useGetEventByIdQuery } from '../store';
import { setSelectedEvent } from '../store/slices/events';
import EventDTO from '../models/event';

interface Props {
  skip?: boolean;
  slug?: boolean;
}

const useGetSelectedEvent = ({ skip = false, slug = false }: Props = {}) => {
  const dispatch = useDispatch();
  const { event_id: eventId } = useParams<{ event_id: string }>();
  const selectedEvent = useSelector((state: RootState) => state.events.selectedEvent);
  // The stored event is only reusable when it matches the event in the current URL.
  // In customer-facing flows the URL param is a slug; in backoffice it is the id.
  // Without this check a stale event (the previously viewed one) leaks into the
  // breadcrumbs and page title when navigating between events.
  const storedMatchesUrl = !!selectedEvent && (
    !eventId || (slug ? selectedEvent.slug === eventId : selectedEvent.id === eventId)
  );
  // List views (dashboard, events table) store a BasicEventDTO cast to EventDTO,
  // which is missing detail-only fields such as has_showtimes. If we skip the
  // fetch for those, pages like EventOrders render the event as if it had no
  // showtimes. Only skip the fetch when the stored event is a full event.
  const storedIsComplete = storedMatchesUrl
    && (selectedEvent as Partial<EventDTO>).has_showtimes !== undefined;
  const { data: event, isLoading: eventLoading } = useGetEventByIdQuery(
    { eventId: eventId || '0', slug },
    { skip: skip || !eventId || storedIsComplete },
  );
  useEffect(() => {
    // Replace the stored event when it's a different event, or when the stored
    // copy is an incomplete (basic) event and we've now fetched the full one.
    if (event && (event.id !== selectedEvent?.id || !storedIsComplete)) {
      dispatch(setSelectedEvent(event));
    }
  }, [event, selectedEvent, dispatch, storedIsComplete]);

  if (skip) {
    return { event: null, eventLoading: false };
  }
  return { event: event || (storedMatchesUrl ? selectedEvent : null), eventLoading };
};

export default useGetSelectedEvent;
